import moment from "moment";
import { useEffect, useState } from "react";
import { controlToastState, DateInput, Modal, Select } from "risa-oasis-ui_v2";
import { ModalId } from "../../../enums/modalId";
import { handleNarIngestionUpload } from "../../../pages/configurations/pages/narConfiguration/actions/handleNarIngestionUpload";
import useFetchPayerSubtypes from "../../../pages/configurations/pages/narConfiguration/hooks/fetchPayerSubtypes";
import {
  extractInsuranceOptions,
  extractPlanOptions,
} from "../../../pages/configurations/pages/narConfiguration/utils/extractPayerOptions";
import { generateNarFilePath } from "../../../pages/configurations/pages/narConfiguration/utils/generateNarFilePath";
import CustomRadioButton from "../../customRadioButton/customRadioButton";
import DragDropUpload from "../../dragDropUpload/dragDropUpload";

interface NarIngestionUploadProps {
  onClose: () => void;
}

const NarIngestionUpload = ({ onClose }: NarIngestionUploadProps) => {
  const [fileType, setFileType] = useState<"pdf" | "csv">("pdf");
  const [selectedInsurance, setSelectedInsurance] = useState<string>("");
  const [selectedPlan, setSelectedPlan] = useState<string>("");
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(
    moment(new Date()).format("MM/DD/YYYY"),
  );
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { data: payerSubtypes } = useFetchPayerSubtypes();

  // Extract insurance and plan options using utility functions
  const insuranceOptions = extractInsuranceOptions(payerSubtypes);
  const planOptions = extractPlanOptions(payerSubtypes, selectedInsurance);

  // Clear plan when insurance changes
  useEffect(() => {
    setSelectedPlan("");
  }, [selectedInsurance]);

  let filePath = "";
  if (selectedInsurance && selectedPlan && selectedDate) {
    let date = moment(selectedDate, "MM/DD/YYYY", true);
    if (!date.isValid()) {
      date = moment(selectedDate);
    }

    if (date.isValid()) {
      const month = date.month() + 1;
      const year = date.year();
      const day = date.date();

      if (!isNaN(month) && !isNaN(year) && !isNaN(day)) {
        filePath = generateNarFilePath(
          selectedInsurance,
          selectedPlan,
          day,
          month,
          year,
          fileType,
          false,
        );
      }
    }
  }

  const handleUploadSuccess = () => {
    controlToastState("nar-ingestion-file-uploaded-success");
  };

  const handleSave = async () => {
    await handleNarIngestionUpload({
      selectedInsurance,
      selectedPlan,
      selectedDate,
      uploadedFiles,
      fileType,
      onSuccess: onClose,
      setIsSubmitting,
    });
  };

  return (
    <Modal
      dialogId={ModalId.NAR_INGESTION_UPLOAD_MODAL}
      title="Upload Grid"
      onSave={handleSave}
      saveButtonText={isSubmitting ? "Submitting..." : "Confirm"}
      cancelText="Cancel"
      onClose={onClose}
      onCancel={onClose}
      disableSave={isSubmitting}
    >
      <div className="flex h-full w-full flex-col gap-2">
        <Select
          id="insurance"
          label="Select the Insurance"
          options={insuranceOptions}
          value={selectedInsurance}
          onOptionChange={(option) => {
            setSelectedInsurance(option?.value || "");
          }}
          placeholder="Select the Insurance"
        />
        <Select
          key={selectedInsurance}
          id="plan"
          label="Select the Plan"
          options={planOptions}
          value={selectedPlan || undefined}
          onOptionChange={(option) => {
            setSelectedPlan(option?.value || "");
          }}
          placeholder="Select the Insurance first to see the plans"
        />
        <div className="w-full overflow-x-hidden">
          <DateInput
            id="date"
            label="Select the Date"
            format="MM/DD/YYYY"
            defaultValue={moment(new Date()).format("MM/DD/YYYY")}
            onChange={(data) => {
              // Format the date to ensure consistent MM/DD/YYYY format
              if (data.value) {
                const date = moment(data.value);
                if (date.isValid()) {
                  setSelectedDate(date.format("MM/DD/YYYY"));
                } else {
                  setSelectedDate(data.value);
                }
              } else {
                setSelectedDate("");
              }
            }}
          />
        </div>
        <div className="nar-ingestion-upload__file-type flex flex-col gap-2">
          <div className="text-x-tiny">Select the File Type</div>
          <div className="flex flex-row gap-2">
            <CustomRadioButton
              name="fileType"
              value="pdf"
              checked={fileType === "pdf"}
              label="Pdf"
              onChange={() => setFileType("pdf")}
            />
            <CustomRadioButton
              name="fileType"
              value="csv"
              checked={fileType === "csv"}
              label="Csv"
              onChange={() => setFileType("csv")}
            />
          </div>
        </div>
        <DragDropUpload
          id="nar-ingestion-upload"
          subText="Drag and drop or click to upload a new file"
          allowedFileTypes={[".pdf", ".csv"]}
          maxFileSize={10 * 1024 * 1024}
          filePath={filePath}
          onUploadSuccess={handleUploadSuccess}
          downloadedURL={(urls) => setUploadedFiles(urls)}
        />
      </div>
    </Modal>
  );
};

export default NarIngestionUpload;
